import {
  Box,
  Menu,
  Button,
  Divider,
  MenuItem,
  useTheme,
  TextField,
  Autocomplete,
  ListItemText,
  svgIconClasses,
} from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridExcelExportMenuItem,
  GridExportMenuItemProps,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
} from '@mui/x-data-grid-premium';
import { Schema } from 'library/types';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import apiService from 'library/api-service';
import TuneIcon from '@mui/icons-material/Tune';
import CheckIcon from '@mui/icons-material/Check';
import { renderAutoCompleteLocaleText } from 'utils';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import type { CustomToolbarSlotProps } from './types';
import { FormattedMessage, useIntl } from 'react-intl';
import { SaveTableTemplateDialog } from './components';
import { gray } from 'providers/theme/themePrimitives';
import useTableSettings from './hooks/use-table-settings';
import type { GridToolbarProps } from '@mui/x-data-grid-premium';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

export type { CustomToolbarSlotProps } from './types';

export type CustomToolbarProps = GridToolbarProps & CustomToolbarSlotProps;

const CustomToolbar = ({
  exportPremiumExcel = false,
  onlyOneExcel = false,
  multiExport = false,
  exportAsOnlyOneExcelHandler = async () => {},
  exportAsExcelHandler = async () => {},
  exportAsCsvHandler = async () => {},
  showTemplates = false,
  pageName,
  apiRef = null,
  singleFilter = false,
  singleFilterComponent,
  excelStyleRules = [],
}: CustomToolbarProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { templates, templatesLoading, refreshTemplates } = useTableSettings({
    enabled: showTemplates,
    pageName,
  });

  // useState hooks
  const [saveOpen, setSaveOpen] = useState(false);
  const [selected, setSelected] = useState<Schema | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [templateMutationLoading, setTemplateMutationLoading] = useState(false);

  // dynamic variables
  const openMenu = Boolean(anchorEl);

  const handleApply = () => {
    if (!apiRef || !selected || templatesLoading || templateMutationLoading) return;

    try {
      apiRef.current.restoreState(JSON.parse(selected.template));
      enqueueSnackbar(<FormattedMessage id="datagrid.appliedTemplate" defaultMessage="Şablon uygulandı." />, {
        variant: 'success',
      });
      setAnchorEl(null);
    } catch {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'datagrid.applyTemplateError',
          defaultMessage: 'Tablo şablonu uygulanırken bir hata oluştu.',
        }),
        { variant: 'error' }
      );
    }
  };

  const handleSave = async (name: string) => {
    if (!apiRef || !pageName || templatesLoading || templateMutationLoading) return;

    if (!name) {
      enqueueSnackbar(
        <FormattedMessage id="datagrid.requiredTemplateName" defaultMessage="Lütfen bir şablon ismi giriniz!" />,
        {
          variant: 'warning',
        }
      );
      return;
    }

    let template;

    try {
      template = apiRef.current.exportState();
    } catch {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'datagrid.saveTemplateError',
          defaultMessage: 'Tablo şablonu kaydedilirken bir hata oluştu.',
        }),
        { variant: 'error' }
      );
      return;
    }

    try {
      setTemplateMutationLoading(true);
      const response = await apiService.createTableTemplate({
        pageName,
        name,
        template,
      });

      if (!response.isSuccess) {
        enqueueSnackbar(
          response.message ||
            intl.formatMessage({
              id: 'datagrid.saveTemplateError',
              defaultMessage: 'Tablo şablonu kaydedilirken bir hata oluştu.',
            }),
          { variant: 'error' }
        );
        return;
      }

      enqueueSnackbar(<FormattedMessage id="datagrid.savedTemplate" defaultMessage="Şablon kaydedildi." />, {
        variant: 'success',
      });
      setSaveOpen(false);
      await refreshTemplates();
    } catch {
      // HTTP errors are surfaced by the shared response interceptor.
    } finally {
      setTemplateMutationLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selected || templatesLoading || templateMutationLoading) return;

    try {
      setTemplateMutationLoading(true);
      const response = await apiService.deleteTableTemplate(selected.id);

      if (!response.isSuccess) {
        enqueueSnackbar(
          response.message ||
            intl.formatMessage({
              id: 'datagrid.deleteTemplateError',
              defaultMessage: 'Tablo şablonu silinirken bir hata oluştu.',
            }),
          { variant: 'error' }
        );
        return;
      }

      enqueueSnackbar(<FormattedMessage id="datagrid.deletedTemplate" defaultMessage="Şablon silindi." />, {
        variant: 'success',
      });
      setSelected(null);
      await refreshTemplates();
    } catch {
      // HTTP errors are surfaced by the shared response interceptor.
    } finally {
      setTemplateMutationLoading(false);
    }
  };

  // sync helper functions
  const ExcelExportMenuItem = ({ hideMenu }: GridExportMenuItemProps<{}>) => (
    <MenuItem
      onClick={() => {
        exportAsExcelHandler();

        // Hide the export menu after the export
        hideMenu?.();
      }}>
      <FormattedMessage id="datagrid.exportAsExcel" defaultMessage="Excel İndir" />
    </MenuItem>
  );

  const CsvExportMenuItem = ({ hideMenu }: GridExportMenuItemProps<{}>) => (
    <MenuItem
      onClick={() => {
        exportAsCsvHandler();

        // Hide the export menu after the export
        hideMenu?.();
      }}>
      <FormattedMessage id="datagrid.exportAsCsv" defaultMessage="CSV İndir" />
    </MenuItem>
  );

  const applyChipLikeStyle = (cell: any, style: { bg: string; fg: string; border: string }) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: style.bg },
    };

    cell.font = {
      ...(cell.font ?? {}),
      bold: true,
      color: { argb: style.fg },
    };

    cell.alignment = {
      ...(cell.alignment ?? {}),
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    };

    cell.border = {
      top: { style: 'thin', color: { argb: style.border } },
      bottom: { style: 'thin', color: { argb: style.border } },
      left: { style: 'thin', color: { argb: style.border } },
      right: { style: 'thin', color: { argb: style.border } },
    };
  };

  // useEffect hooks
  useEffect(() => {
    setSelected(null);
    setAnchorEl(null);
    setSaveOpen(false);
  }, [pageName]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && showTemplates && (!pageName || !apiRef)) {
      console.error('CustomToolbar requires pageName and apiRef when showTemplates is enabled.');
    }
  }, [apiRef, pageName, showTemplates]);

  useEffect(() => {
    if (selected && !templates.some((template) => template.id === selected.id)) {
      setSelected(null);
    }
  }, [selected, templates]);

  return (
    <GridToolbarContainer sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
      <Box>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        {exportPremiumExcel && (
          <Button
            startIcon={<SaveAltIcon />}
            size="small"
            sx={{
              '& .MuiButton-startIcon': {
                mr: 0.25, // default 1 (8px)
              },
            }}>
            <GridExcelExportMenuItem
              options={{
                exceljsPostProcess: async ({ worksheet }) => {
                  const fieldToColumnNumber = new Map<string, number>();

                  if (apiRef?.current) {
                    const exportableColumns = apiRef.current.getAllColumns().filter((col) => !col.disableExport);

                    exportableColumns.forEach((col, index) => {
                      fieldToColumnNumber.set(col.field, index + 1);
                    });
                  }

                  excelStyleRules.forEach((rule) => {
                    const columnNumber = fieldToColumnNumber.get(rule.field);
                    if (!columnNumber) return;

                    if (rule.width) {
                      worksheet.getColumn(columnNumber).width = rule.width;
                    }
                  });

                  worksheet.eachRow((row) => {
                    if (row.number === 1) return;

                    let nextRowHeight = row.height;

                    excelStyleRules.forEach((rule) => {
                      const columnNumber = fieldToColumnNumber.get(rule.field);
                      if (!columnNumber) return;

                      const cell = row.getCell(columnNumber);
                      const cellValue = typeof cell.value === 'string' ? cell.value : String(cell.value ?? '');

                      if (rule.wrapText || cellValue.includes('\n')) {
                        cell.alignment = {
                          ...(cell.alignment ?? {}),
                          wrapText: true,
                          horizontal: rule.horizontal ?? 'left',
                          vertical: rule.vertical ?? 'top',
                        };
                      }

                      if (rule.getRowHeight) {
                        const calculatedHeight = rule.getRowHeight(cellValue);
                        if (calculatedHeight) {
                          nextRowHeight = Math.max(nextRowHeight ?? 0, calculatedHeight);
                        }
                      }

                      if (rule.getCellStyle) {
                        const style = rule.getCellStyle(cellValue);
                        if (style) {
                          applyChipLikeStyle(cell, style);
                        }
                      }
                    });

                    if (nextRowHeight) {
                      row.height = nextRowHeight;
                    }
                  });
                },
              }}
            />
          </Button>
        )}
        {onlyOneExcel && (
          <Button startIcon={<SaveAltIcon />} size="small" onClick={exportAsOnlyOneExcelHandler}>
            <FormattedMessage id="datagrid.toolbarExport" defaultMessage="İndir" />
          </Button>
        )}
        {multiExport && (
          <GridToolbarExportContainer
            slotProps={{
              tooltip: {
                placement: 'top',
              },
            }}>
            <ExcelExportMenuItem />
            <CsvExportMenuItem />
          </GridToolbarExportContainer>
        )}
        {showTemplates && (
          <>
            <Button
              startIcon={<TuneIcon />}
              size="small"
              disabled={!pageName || !apiRef}
              onClick={(e) => setAnchorEl(e.currentTarget)}>
              <FormattedMessage id="datagrid.tableSettings" defaultMessage="Tablo Ayarları" />
            </Button>

            <Menu anchorEl={anchorEl} open={openMenu} onClose={() => setAnchorEl(null)}>
              <Box sx={{ px: 2, pt: 1.5, width: 360 }}>
                <Autocomplete
                  size="small"
                  options={templates}
                  value={selected}
                  loading={templatesLoading}
                  disabled={templateMutationLoading}
                  {...renderAutoCompleteLocaleText()}
                  onChange={(_, v) => setSelected(v)}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        '& .MuiButtonBase-root': {
                          border: 'none',
                          backgroundColor: 'inherit',
                          width: '2.25rem',
                          height: '2.25rem',
                          padding: '0.25rem',
                          [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                          },
                        },
                      }}
                      label={<FormattedMessage id="datagrid.selectTemplate" defaultMessage="Şablon seç" />}
                    />
                  )}
                />
              </Box>

              <Divider sx={{ my: 1 }} />

              <MenuItem
                onClick={handleApply}
                disabled={!selected || !apiRef || templatesLoading || templateMutationLoading}>
                <CheckIcon fontSize="small" style={{ marginRight: 8 }} />
                <ListItemText primary={<FormattedMessage id="datagrid.apply" defaultMessage="Uygula" />} />
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setSaveOpen(true);
                  setAnchorEl(null);
                }}
                disabled={!apiRef || !pageName || templatesLoading || templateMutationLoading}>
                <SaveAltIcon fontSize="small" style={{ marginRight: 8 }} />
                <ListItemText
                  primary={<FormattedMessage id="datagrid.saveNewTemplate" defaultMessage="Yeni şablon kaydet" />}
                />
              </MenuItem>

              <MenuItem onClick={handleDelete} disabled={!selected || templatesLoading || templateMutationLoading}>
                <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
                <ListItemText primary={<FormattedMessage id="datagrid.delete" defaultMessage="Sil" />} />
              </MenuItem>
            </Menu>

            <SaveTableTemplateDialog
              open={saveOpen}
              loading={templateMutationLoading}
              onClose={() => setSaveOpen(false)}
              onSave={handleSave}
            />
          </>
        )}
      </Box>
      <Box>{singleFilter && singleFilterComponent}</Box>
    </GridToolbarContainer>
  );
};

export default CustomToolbar;
